import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Col, Row } from 'react-bootstrap'
import dynamic from 'next/dynamic'
import { useLazyQuery } from '@apollo/client'

import { GET_AUTHOR_ARTICLES, GET_AUTHOR_FANTASY_ARTICLES } from '@graphql/author/author.query'
import { isBottomReached } from '@shared-submodule/utils'
import { articleLoader } from '@shared/libs/allLoader'
import articleStyles from '@shared/components/article/style.module.scss'
import ClientCommonNav from '@shared/components/commonNav/client'
import NoData from '@shared/components/noData'
const ArticleMedium = dynamic(() => import('@shared/components/article/articleMedium'), { loading: () => articleLoader(['s']) })

const TABS_KEY = { articles: 'articles', fantasyArticles: 'fantasyArticles' }
const NAV_TABS = [
  { navItem: 'Articles', internalName: 'articles' },
  { navItem: 'Fantasy Articles', internalName: 'fantasyArticles' }
]
function AuthorArticles({ articles: allArticles, authorID }) {
  const [tabKey, setTabKey] = useState(TABS_KEY.articles)
  const [articles, setArticles] = useState(allArticles?.aResults)
  const [fantasyArticles, setFantasyArticles] = useState([])
  const isLoading = useRef(false)
  const isReachedBottom = useRef(false)
  const payload = useRef({ nLimit: 10, nSkip: 1, iAuthorDId: authorID })
  const [getArticle, { data: articleData, loading: isArticleLoading }] = useLazyQuery(GET_AUTHOR_ARTICLES, {
    variables: { input: payload.current }
  })
  const latestArticle = useRef(articles.length)
  const latestFantasyArticle = useRef(10)
  const [getFantasyArticle, { data: fantasyArticleData, loading: isFantasyArticleLoading }] = useLazyQuery(GET_AUTHOR_FANTASY_ARTICLES)

  useEffect(() => {
    if (tabKey === 'fantasyArticles') {
      payload.current = { nLimit: 10, nSkip: 1, iAuthorDId: authorID }
      getFantasyArticle({ variables: { input: { nLimit: 10, nSkip: 1, iAuthorDId: authorID } } })
    }
  }, [tabKey])

  useEffect(() => {
    isLoading.current = false
    if (tabKey === 'fantasyArticles') {
      isBottomReached(fantasyArticles[fantasyArticles.length - 1]?._id, isReached)
    } else {
      isBottomReached(articles[articles.length - 1]?._id, isReached)
    }
  }, [articles, fantasyArticles])

  useEffect(() => {
    if (tabKey === 'fantasyArticles') {
      if (isReachedBottom.current && fantasyArticleData) {
        setFantasyArticles([...fantasyArticles, ...fantasyArticleData?.getAuthorFantasyArticles?.aResults])
        isLoading.current = false
        isReachedBottom.current = false
      } else if (!isLoading.current && fantasyArticleData) {
        setFantasyArticles(fantasyArticleData?.getAuthorFantasyArticles?.aResults)
      }
      latestFantasyArticle.current = fantasyArticleData?.getAuthorFantasyArticles?.aResults?.length
    } else {
      if (isReachedBottom.current && articleData) {
        setArticles([...articles, ...articleData?.getAuthorArticles?.aResults])
        isLoading.current = false
        isReachedBottom.current = false
      } else if (!isLoading.current && articleData) {
        setArticles(articleData?.getAuthorArticles?.aResults)
      }
      latestArticle.current = articleData?.getAuthorArticles?.aResults?.length >= 0 ? articleData?.getAuthorArticles?.aResults?.length : false || allArticles?.aResults?.length
    }
  }, [articleData, fantasyArticleData])

  function isReached(reach) {
    if (tabKey === 'fantasyArticles') {
      if (reach && !isLoading.current && latestFantasyArticle.current === payload.current.nLimit) {
        isLoading.current = true
        isReachedBottom.current = true
        payload.current = { ...payload.current, nSkip: payload.current.nSkip + 1 }
        getFantasyArticle({ variables: { input: payload.current } })
      }
    } else {
      if (reach && !isLoading.current && latestArticle.current === payload.current.nLimit) {
        isLoading.current = true
        isReachedBottom.current = true
        payload.current = { ...payload.current, nSkip: payload.current.nSkip + 1 }
        getArticle({ variables: { input: payload.current } })
      }
    }
  }

  return (
    <div>
      <ClientCommonNav items={NAV_TABS} active={tabKey} onTabChange={(e) => setTabKey(e)} />
      {
        tabKey === TABS_KEY.articles ? (
          <div>
            {articles.length === 0 ? (
              <div className={articleStyles.article}><NoData /></div>
            ) : (
              <Row>
                {
                  articles.map((article, index) => {
                    if (index === 0) {
                      return (
                        <Col sm={12} key={article._id}>
                          <ArticleMedium data={article} key={article._id} isSmall />
                        </Col>
                      )
                    } else if (index > 1 && (index % 7 === 0 || (index - 1) % 7 === 0)) {
                      return (
                        <Col md={6} key={article._id}>
                          <ArticleMedium data={article} isSmall />
                        </Col>
                      )
                    } else {
                      return (
                        <ArticleMedium key={article._id} data={article} isSmall />
                      )
                    }
                  })
                }
              </Row>
            )}
            {(isArticleLoading) && articleLoader(['s'])}
          </div>
        ) : (
          <div>
            {!isFantasyArticleLoading && fantasyArticles?.length === 0 ? (
              <div className={articleStyles.article}><NoData /></div>
            ) : (
              <Row>
                {
                  fantasyArticles.map((article, index) => {
                    if (index === 0) {
                      return (
                        <Col sm={12} key={article._id}>
                          <ArticleMedium key={article._id} data={article} isSmall />
                        </Col>
                      )
                    } else if (index > 1 && (index % 7 === 0 || (index - 1) % 7 === 0)) {
                      return (
                        <Col md={6} key={article._id}>
                          <ArticleMedium key={article._id} data={article} isSmall />
                        </Col>
                      )
                    } else {
                      return (
                        <ArticleMedium key={article._id} data={article} isSmall />
                      )
                    }
                  })
                }
              </Row>
            )}
            {(isFantasyArticleLoading) && articleLoader(['s'])}
          </div>
        )
      }
    </div>
  )
}

AuthorArticles.propTypes = {
  articles: PropTypes.object,
  fantasyArticles: PropTypes.object,
  authorID: PropTypes.string
}
AuthorArticles.defaultProps = {
  articles: [],
  fantasyArticles: []
}

export default AuthorArticles
